import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PocketsService } from "../services/pocketsService";
import type { Pocket } from "../types/pockets-api.types";

// Shared by `PocketsPreviewRow` and `ActivatePocketsBanner` — both fired
// `/internal/pockets?limit=10` on mount. Short stale window so balance
// changes (topup/withdrawal) appear without an explicit refresh; mutating
// components should call `invalidateOrganizationPockets(orgId)`.
const STALE_AFTER_MS = 30_000;
const PAGE_SIZE = 10;

type CacheEntry = {
  pockets: Pocket[];
  fetchedAt: number;
};

const entries = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<Pocket[]>>();
const listeners = new Map<string, Set<() => void>>();

function notify(orgId: string): void {
  const set = listeners.get(orgId);
  if (!set) return;
  for (const fn of set) fn();
}

async function loadFromServer(): Promise<Pocket[]> {
  const response = await PocketsService.fetchPockets({ limit: PAGE_SIZE });
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

async function ensureFresh(orgId: string): Promise<Pocket[]> {
  const existing = entries.get(orgId);
  if (existing && Date.now() - existing.fetchedAt < STALE_AFTER_MS) {
    return existing.pockets;
  }

  let promise = inflight.get(orgId);
  if (!promise) {
    promise = loadFromServer()
      .then((pockets) => {
        entries.set(orgId, { pockets, fetchedAt: Date.now() });
        return pockets;
      })
      .finally(() => {
        inflight.delete(orgId);
      });
    inflight.set(orgId, promise);
  }

  const pockets = await promise;
  notify(orgId);
  return pockets;
}

export function invalidateOrganizationPockets(orgId: string): void {
  entries.delete(orgId);
  notify(orgId);
}

export function useOrganizationPockets(): {
  pockets: Pocket[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const { user } = useAuth();
  const orgId = user?.organization_id ?? user?.organization?.id ?? null;

  const [pockets, setPockets] = useState<Pocket[]>(() => (orgId ? (entries.get(orgId)?.pockets ?? []) : []));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      // Reset on logout / org switch so the previous org's pockets do not
      // leak into the new session.
      /* eslint-disable react-hooks-eslint/set-state-in-effect */
      setPockets([]);
      setIsLoading(false);
      /* eslint-enable react-hooks-eslint/set-state-in-effect */
      return;
    }

    let cancelled = false;

    const subscribe = () => {
      const next = entries.get(orgId)?.pockets ?? [];
      if (!cancelled) setPockets(next);
    };

    const set = listeners.get(orgId) ?? new Set();
    set.add(subscribe);
    listeners.set(orgId, set);

    setIsLoading(true);
    setError(null);
    ensureFresh(orgId)
      .then((next) => {
        if (cancelled) return;
        setPockets(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(HttpApiService.getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      const next = listeners.get(orgId);
      if (next) {
        next.delete(subscribe);
        if (next.size === 0) listeners.delete(orgId);
      }
    };
  }, [orgId]);

  const refresh = async (): Promise<void> => {
    if (!orgId) return;
    invalidateOrganizationPockets(orgId);
    try {
      const fresh = await ensureFresh(orgId);
      setPockets(fresh);
    } catch (err: unknown) {
      setError(HttpApiService.getErrorMessage(err));
    }
  };

  return { pockets, isLoading, error, refresh };
}
